/**
 * Client API pour le Learning Service
 * Couche d'abstraction pour les appels HTTP bruts
 */

import { HttpClient } from '@sphere/shared/data-access/http'
import type { OffsetPaginationResponseDto } from '@sphere/shared/domain'
import type {
  ActivityTemplate,
  CreateTemplateDto,
  UpdateTemplateDto,
  ListTemplatesDto,
  ActivityCategoryRef,
  CreateActivityCategoryDto,
  UpdateActivityCategoryDto,
  ListActivityCategoriesDto,
  LearningPath,
  CreateLearningPathDto,
  UpdateLearningPathDto,
  ListLearningPathsDto,
} from '@sphere/learning/domain'

import { activitiesClient, activityTypesClient } from './activities.client'
import { attemptsClient } from './attempts.client'
import { assignmentsClient } from './assignments.client'
import { referencesClient } from './references.client'

export const learningClient = {
  // Learning Paths
  learningPaths: {
    list: (params?: ListLearningPathsDto) =>
      HttpClient.get<OffsetPaginationResponseDto<LearningPath>>(
        '/learning/learning-paths',
        { params }
      ),

    getById: (id: string) =>
      HttpClient.get<LearningPath>(`/learning/learning-paths/${id}`),

    create: (data: CreateLearningPathDto) =>
      HttpClient.post<LearningPath>('/learning/learning-paths', data),

    update: (id: string, data: UpdateLearningPathDto) =>
      HttpClient.patch<LearningPath>(`/learning/learning-paths/${id}`, data),

    delete: (id: string) =>
      HttpClient.delete<void>(`/learning/learning-paths/${id}`),
  },

  // Activities
  activities: activitiesClient,

  // Activity Types
  activityTypes: activityTypesClient,

  // Attempts
  attempts: attemptsClient,

  // Activity Templates (CRUD Admin)
  activityTemplates: {
    list: (params?: ListTemplatesDto) =>
      HttpClient.get<OffsetPaginationResponseDto<ActivityTemplate>>(
        '/learning/activity-templates',
        { params }
      ),

    getById: (id: string) =>
      HttpClient.get<ActivityTemplate>(`/learning/activity-templates/${id}`),

    create: (data: CreateTemplateDto) =>
      HttpClient.post<ActivityTemplate>('/learning/activity-templates', data),

    update: (id: string, data: UpdateTemplateDto) =>
      HttpClient.patch<ActivityTemplate>(
        `/learning/activity-templates/${id}`,
        data
      ),

    delete: (id: string) =>
      HttpClient.delete<void>(`/learning/activity-templates/${id}`),
  },

  // Activity Categories (CRUD Admin)
  activityCategories: {
    list: (params?: ListActivityCategoriesDto) =>
      HttpClient.get<OffsetPaginationResponseDto<ActivityCategoryRef>>(
        '/learning/activity-categories',
        { params }
      ),

    getById: (id: string) =>
      HttpClient.get<ActivityCategoryRef>(
        `/learning/activity-categories/${id}`
      ),

    create: (data: CreateActivityCategoryDto) =>
      HttpClient.post<ActivityCategoryRef>(
        '/learning/activity-categories',
        data
      ),

    update: (id: string, data: UpdateActivityCategoryDto) =>
      HttpClient.patch<ActivityCategoryRef>(
        `/learning/activity-categories/${id}`,
        data
      ),

    delete: (id: string) =>
      HttpClient.delete<void>(`/learning/activity-categories/${id}`),
  },

  // Reference Data
  reference: referencesClient,

  // Activity-Lesson Links
  activityLessonLinks: assignmentsClient.activityLessonLinks,

  // Assignments
  activityUserAssignments: assignmentsClient.activityUserAssignments,
  activityTeachingAssignments: assignmentsClient.activityTeachingAssignments,
}
